<script setup lang="ts">
import RoutePageHeader from '@/components/RoutePageHeader.vue'
import SiteFooter from '@/components/static/SiteFooter.vue'
import { useSafeAreaHeaderClearance } from '@/services/safeAreaService'

interface Props {
  isOpen: boolean
}

const props = defineProps<Props>()
const pageTopPadding = useSafeAreaHeaderClearance(48)

const emit = defineEmits<{
  'open-static-site': [pageId: string]
}>()

const openStaticSite = (pageId: string) => {
  emit('open-static-site', pageId)
}
</script>

<template>
  <div
    v-if="props.isOpen"
    class="flex h-full min-h-0 flex-col overflow-hidden bg-[#FEFEFE]"
    :style="{ paddingTop: pageTopPadding }"
  >
    <div class="min-h-0 flex-1 overflow-y-auto">
      <RoutePageHeader
        eyebrow="About routecamp"
        title="Plan, document, and share your trips without locking your data away."
        prominent
      />

      <section class="px-5 py-14 sm:px-8 sm:py-18">
        <div class="mx-auto max-w-5xl">
          <div class="grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div>
              <span
                class="mb-2 block font-mono text-[11px] uppercase tracking-widest text-stone-500"
              >
                Interoperability
              </span>
              <h2 class="font-mono text-2xl font-semibold text-stone-900 sm:text-3xl">
                Import and export is a core concept, not an afterthought.
              </h2>
            </div>
            <div class="space-y-4 text-sm leading-relaxed text-stone-700 sm:text-base">
              <p>
                Interoperability matters. Outdoor data becomes more valuable over time when you can
                move it, back it up, reuse it in other tools, and keep access to it long after a
                single app changes.
              </p>
              <p>
                We designed routecamp to be GPX-compatible for most of the route and trip data we
                work with, and we treat data import and export as a core concept of the platform.
                The goal is simple: your tracks, routes, and trip records should remain useful
                outside routecamp too.
              </p>
              <p>
                That means routecamp should help you work with your existing files, not trap them.
                We want the platform to fit into a broader outdoor-tool workflow instead of forcing
                a closed ecosystem.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="border-y border-stone-200/70 bg-stone-50 px-5 py-14 sm:px-8 sm:py-18">
        <div class="mx-auto max-w-5xl">
          <div class="grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div>
              <span
                class="mb-2 block font-mono text-[11px] uppercase tracking-widest text-stone-500"
              >
                Collaboration
              </span>
              <h2 class="font-mono text-2xl font-semibold text-stone-900 sm:text-3xl">
                routecamp was designed for collaboration from the ground up.
              </h2>
            </div>
            <div class="space-y-4 text-sm leading-relaxed text-stone-700 sm:text-base">
              <p>
                Many route tools treat collaboration as an extra sharing layer added after the core
                product is already finished. routecamp takes a different approach. Planning,
                documenting, discussing, and publishing trips together is part of the foundation.
              </p>
              <p>
                Collections, shared route planning, and collaborative curation are central to how
                the platform works. The goal is to make it easy for friends, clubs, teams, and
                communities to build up a useful map of routes and trips together instead of passing
                files around in isolated silos.
              </p>
              <p>
                That collaborative perspective also shapes the product direction: routecamp should
                help people coordinate, contribute context, and maintain shared knowledge over time,
                not just store one person's files.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="px-5 py-14 sm:px-8 sm:py-18">
        <div class="mx-auto max-w-5xl">
          <div class="grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div>
              <span
                class="mb-2 block font-mono text-[11px] uppercase tracking-widest text-stone-500"
              >
                Open Data
              </span>
              <h2 class="font-mono text-2xl font-semibold text-stone-900 sm:text-3xl">
                We believe in open data, and we try to build in that spirit.
              </h2>
            </div>
            <div class="space-y-4 text-sm leading-relaxed text-stone-700 sm:text-base">
              <p>
                routecamp depends on open foundations that already do important work for the outdoor
                and mapping world. That includes
                <a
                  href="https://www.openstreetmap.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-emerald-800 underline underline-offset-2 hover:text-emerald-700"
                >
                  OpenStreetMap
                </a>
                for map data,
                <a
                  href="https://brouter.de/brouter/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-emerald-800 underline underline-offset-2 hover:text-emerald-700"
                >
                  BRouter
                </a>
                for routing, and the open-source software stack we use to build and run routecamp.
              </p>
              <p>
                We do not see routecamp as something that should only consume value from that
                ecosystem. Our long-term goal is to contribute back where we can and, where it fits
                OpenStreetMap's model and quality expectations, bring some information that
                currently lives only inside routecamp back to OpenStreetMap.
              </p>
              <p>
                For us, open data is not only about using great tools. It is also about
                interoperability, transparency, shared infrastructure, and building something that
                can participate in a broader commons instead of becoming an isolated silo.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="border-y border-stone-200/70 bg-stone-50 px-5 py-14 sm:px-8 sm:py-18">
        <div class="mx-auto max-w-5xl">
          <div class="grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div>
              <span
                class="mb-2 block font-mono text-[11px] uppercase tracking-widest text-stone-500"
              >
                Data Privacy
              </span>
              <h2 class="font-mono text-2xl font-semibold text-stone-900 sm:text-3xl">
                Privacy really matters when your data describes where you go.
              </h2>
            </div>
            <div class="space-y-4 text-sm leading-relaxed text-stone-700 sm:text-base">
              <p>
                Route data, trip logs, photos, timestamps, and location histories can reveal a lot
                about a person. That is why data privacy is not a side topic for routecamp. It
                affects product decisions, infrastructure choices, and how we think about defaults.
              </p>
              <p>
                We aim to follow data minimization principles and collect or process only the
                personal data that is reasonably necessary to operate and secure the Site and to
                improve your experience with it. Protecting user privacy is a core principle of
                routecamp.
              </p>
              <p>
                If you want more detail on how we handle this, read our

                <button
                  class="cursor-pointer text-emerald-800 underline underline-offset-2 hover:text-emerald-700"
                  @click="openStaticSite('privacy-policy')"
                >
                  Privacy Policy
                </button>
                and

                <button
                  class="cursor-pointer text-emerald-800 underline underline-offset-2 hover:text-emerald-700"
                  @click="openStaticSite('eu-data-protection')"
                >
                  EU Data Protection
                </button>
                page.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="px-5 py-14 sm:px-8 sm:py-18">
        <div class="mx-auto max-w-5xl">
          <div class="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
            <span class="mb-2 block font-mono text-[11px] uppercase tracking-widest text-stone-500">
              Why this matters
            </span>
            <h2 class="font-mono text-2xl font-semibold text-stone-900 sm:text-3xl">
              A route platform should help you organize your adventures without taking ownership of
              them.
            </h2>
            <p class="mt-5 max-w-3xl text-sm leading-relaxed text-stone-700 sm:text-base">
              routecamp is built around long-term usefulness: keep your files portable, keep
              collaboration practical, and keep privacy expectations high. That philosophy shapes
              the product as much as the map does.
            </p>
          </div>
        </div>
      </section>

      <SiteFooter @open-static-site="openStaticSite" />
    </div>
  </div>
</template>
